import { useCallback } from 'react';
import type { OrderablePackage } from '@mypoz/shared';
import { gramsToKg, totalWeightGrams } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';

import { useFormat } from '@/lib/format.js';

/**
 * The weight part of an order's selection summary (phone order layout).
 *
 * A package has no weight until the warehouse takes it in, and adding those up as zero printed
 * "0.0 kg" for a selection that is simply not weighed yet. So: the total when every package has a
 * weight (or nothing is selected), "so far" when only some do, and "not known yet" when none do.
 * Display only: the total itself is still `totalWeightGrams`.
 */
export function useSelectionWeight(): (packages: readonly OrderablePackage[]) => string {
  const { t } = useTranslation();
  const fmt = useFormat();

  return useCallback(
    (packages) => {
      // Must match `totalWeightGrams`, which counts a missing weight (`undefined`) as 0.
      const weighed = packages.filter((item) => item.weightGrams !== undefined).length;
      if (packages.length > 0 && weighed === 0) return t('orders.newOrder.weightUnknown');
      const value = fmt.number(gramsToKg(totalWeightGrams(packages)), 1);
      return weighed < packages.length
        ? t('orders.newOrder.weightKgSoFar', { value })
        : t('orders.newOrder.weightKg', { value });
    },
    [t, fmt],
  );
}
